/* useResizeOrFallback.ts */

/* React */
import {
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

/* Utils */
import { ResizeContext } from "src/utils/provider/resize-provider/ResizeProvider";

/**
 * Returns chart dimensions (height, width) and a containerRef to attach to
 * the chart's root element.
 *
 * When wrapped in a ResizeProvider:
 *   - height comes from the shared provider state (supports drag-to-resize).
 *   - width is measured locally via a per-instance ResizeObserver so that each
 *     ChartWrapper inside a Swiper reliably tracks its own container width.
 *
 * Without a ResizeProvider (fallback):
 *   - Both height and width are measured locally via ResizeObserver.
 */
export const useResizeOrFallback = () => {
  const context = useContext(ResizeContext);

  /* Always-stable local ref attached to the IonCard by ChartWrapper. */
  const containerRef = useRef<HTMLDivElement>(null);
  /* Used only in fallback mode where ResizeProvider is absent. */
  const resizerRef = useRef<HTMLDivElement>(null);

  const [localHeight, setLocalHeight] =
    useState<number>(420);
  const [localWidth, setLocalWidth] = useState<number>(0);

  /* Per-instance width observer — always active, context-mode or not. */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      if (entry?.contentRect.width > 0) {
        setLocalWidth(entry.contentRect.width);
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* Drag-to-resize — only used in fallback mode (no ResizeProvider). */
  useEffect(() => {
    if (context) return;

    const resizerEl = resizerRef.current;
    if (!resizerEl) return;

    let startY = 0;
    let startHeight = 0;

    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      startY = e.clientY;
      startHeight = localHeight;

      const onMouseMove = (e: MouseEvent) => {
        const deltaY = e.clientY - startY;
        setLocalHeight(
          Math.max(200, startHeight + deltaY)
        );
      };

      const onMouseUp = () => {
        document.removeEventListener(
          "mousemove",
          onMouseMove
        );
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    };

    resizerEl.addEventListener("mousedown", onMouseDown);
    return () =>
      resizerEl.removeEventListener(
        "mousedown",
        onMouseDown
      );
  }, [context, localHeight]);

  if (context) {
    return {
      containerRef,
      resizerRef: context.resizerRef,
      /* Shared height enables synchronised drag-to-resize across slides. */
      height: context.height,
      width: localWidth,
      fromContext: true as const,
    };
  }

  return {
    containerRef,
    resizerRef,
    height: localHeight,
    width: localWidth,
    fromContext: false as const,
  };
};
